import { Plane, Vector3 } from "three";
import { BaseObject } from "./objects";
export class ClippingGroup {
    constructor() {
        /**
         * Internal enabled flag
         */
        this.enabled = false;
        /**
         * Set of objects part of the clipping group
         */
        this.objects = new Set();
        /**
         * The list of planes that is shared with all threejs materials for all objects in the clipping group
         */
        this.threePlanes = [];
        /**
         * An array to store the set of converted clipping planes while in the disabled state to make the behavior of
         * resolvePlanes easier to reason about.
         */
        this.disabledThreePlanes = [];
        this.planes = [];
    }
    addObject(object) {
        if (object instanceof BaseObject) {
            // Propagate a reference to the threePlanes array so we can change the array without having to manually
            // walk object trees every frame
            object.setClippingPlanes(this.threePlanes);
        }
        this.objects.add(object);
    }
    removeObject(object) {
        if (object instanceof BaseObject) {
            // Give all objects an empty array to disable clipping
            object.setClippingPlanes([]);
        }
        this.objects.delete(object);
    }
    clear() {
        for (const object of this.objects) {
            this.removeObject(object);
        }
    }
    getEnabled() {
        return this.enabled;
    }
    setEnabled(enabled) {
        // Handle when the enabled status is actually changed
        if (enabled && !this.enabled) {
            // Copy the planes out of the backup storage to re-enable the clipping group
            for (const plane of this.disabledThreePlanes) {
                this.threePlanes.push(plane);
            }
            // Clear the backup storage ready for next time we disable the clipping group.
            this.disabledThreePlanes.length = 0;
            // Invalidate the materials for all objects so the updated planes will take effect
            for (const object of this.objects) {
                if (object instanceof BaseObject) {
                    object.invalidateMaterials();
                }
            }
        }
        else if (!enabled && this.enabled) {
            // Copy the planes into the backup storage for when the clipping group is disabled
            for (const plane of this.threePlanes) {
                this.disabledThreePlanes.push(plane);
            }
            // Clear the resolved planes array shared to the three js materials to disable clipping. All relevant
            // materials have a reference to this.threePlanes, so we have to clear in place.
            this.threePlanes.length = 0;
            // Invalidate the materials for all objects so the updated planes will take effect
            for (const object of this.objects) {
                if (object instanceof BaseObject) {
                    object.invalidateMaterials();
                }
            }
        }
        // Update the flag
        this.enabled = enabled;
    }
    resolvePlanes() {
        // Depending on whether the group is enabled or disabled we resolve into one of the two buffers
        const planes = this.enabled ? this.threePlanes : this.disabledThreePlanes;
        // Clear the buffer, so we can replace the contents with our new set of planes
        planes.length = 0;
        // Iterate over the user exposed set of planes and convert them to three.js's representation of a plane, storing
        // them into the chosen plane list
        for (const plane of this.planes) {
            // Extract into threejs types
            const position = new Vector3(plane.position.x, plane.position.y, plane.position.z);
            const normal = new Vector3(plane.normal.x, plane.normal.y, plane.normal.z);
            // Construct a plane from our position+normal representation
            const threePlane = new Plane().setFromNormalAndCoplanarPoint(normal, position);
            // Add to the chosen buffer
            planes.push(threePlane);
        }
        // Invalidate the materials for all objects so the updated planes will take effect
        for (const object of this.objects) {
            if (object instanceof BaseObject) {
                object.invalidateMaterials();
            }
        }
    }
}
